
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type SsaoShaderUniforms = {
	uDepthMap: WebGLUniformLocation,
	uNormalMap: WebGLUniformLocation,
	uNoiseMap: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uProjectionMatrix: WebGLUniformLocation,
	uInvProjection: WebGLUniformLocation,
	uKernel: WebGLUniformLocation[],
	uRadius: WebGLUniformLocation,
	uBias: WebGLUniformLocation,
	uNoiseScale: WebGLUniformLocation,
	uTaaFrameIndex: WebGLUniformLocation
};

export type SsaoShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type SsaoShaderSettings = string[];

export class SsaoShader extends ShaderBase<SsaoShaderUniforms, SsaoShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uDepthMap;
uniform sampler2D uNormalMap;
uniform sampler2D uNoiseMap;

uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform mat4 uInvProjection;

uniform vec3 uKernel[16];
uniform float uRadius;
uniform float uBias;
uniform vec2 uNoiseScale;

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Cleared depth (= 1.0) means "no geometry was drawn" — callers should discard
// or treat this as background BEFORE calling these functions. The reconstruction
// itself still works at depth == 1.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---
// --- begin: temporal_jitter.glsl ---
// Shared per-frame rotation helper for variance-fed temporal effects (PCF
// shadow taps, SSAO hemisphere orientation, etc.). The same rotation is used
// in multiple places so they stay coherent — see project_temporal_pcf_shadows
// memory for the underlying pattern.

#ifndef TEMPORAL_JITTER_INCLUDED
#define TEMPORAL_JITTER_INCLUDED

// Per-frame rotation index, sourced from Camera.taaFrameIndex. 0 means "TAA
// off, no temporal jitter" — the helper returns identity so consumers fall
// back bit-identically to their pre-temporal sampling pattern. Nonzero
// advances the rotation; combined with the per-pixel hash below this gives
// every pixel a different rotation every frame, and TAA's reprojected-history
// blend averages the resulting angular variance into supersampled output.
uniform int uTaaFrameIndex;

// Build a 2D rotation matrix mixing per-frame and per-pixel terms. Per-pixel
// IGN dominates (×16 weight) so neighbouring pixels see distinct angles
// within a single frame — that keeps TAA's 3×3 neighborhood AABB wide enough
// to not clamp the temporal change. Per-frame term decorrelates successive
// frames so TAA averages distinct sample patterns. Returns identity when the
// index is zero, which is the only path taken when TAA is off.
mat2 temporal_jitter_matrix() {
	if (uTaaFrameIndex == 0) {
		return mat2(1.0, 0.0, 0.0, 1.0);
	}
	// Interleaved gradient noise (Jiminez 2014) — cheap blue-noise-ish hash.
	float ign = fract(52.9829189 * fract(0.06711056 * gl_FragCoord.x + 0.00583715 * gl_FragCoord.y));
	float a = (float(uTaaFrameIndex) + ign * 16.0) * 2.39996323;
	float c = cos(a);
	float s = sin(a);
	return mat2(c, s, -s, c);
}

#endif // TEMPORAL_JITTER_INCLUDED

// --- end: temporal_jitter.glsl ---

void main(void) {
	float depth = texture(uDepthMap, vTexCoord).r;
	// Discard background pixels — cleared depth == 1.0 means no geometry.
	if (depth >= 1.0) {
		fragColor = vec4(1.0);
		return;
	}

	vec3 viewPos = viewPosFromDepth(depth, vTexCoord, uInvProjection);
	vec4 normalSample = texture(uNormalMap, vTexCoord);
	// gNormal.w < 0 is the terrain sentinel — skip SSAO entirely for terrain to
	// avoid face-boundary artifacts on low-tessellation heightmap geometry.
	if (normalSample.a < 0.0) {
		fragColor = vec4(1.0);
		return;
	}
	vec3 worldNormal = normalSample.rgb;
	vec3 viewNormal = normalize((uViewMatrix * vec4(worldNormal, 0.0)).xyz);

	vec3 randomVec = texture(uNoiseMap, vTexCoord * uNoiseScale).xyz;

	// Rotate the noise vector per-pixel-per-frame so consecutive frames sample
	// distinct hemisphere orientations; TAA averages the variance. The shared
	// temporal_jitter helper returns identity when TAA is off, so AO output is
	// bit-identical to the pre-temporal behaviour in that path.
	randomVec.xy = temporal_jitter_matrix() * randomVec.xy;

	// Build TBN matrix to orient hemisphere along surface normal
	vec3 tangent = normalize(randomVec - viewNormal * dot(randomVec, viewNormal));
	vec3 bitangent = cross(viewNormal, tangent);
	mat3 TBN = mat3(tangent, bitangent, viewNormal);

	// Scale radius with distance to maintain roughly consistent screen-space coverage.
	// Coefficient 0.05 (down from 0.15) and cap 4× keep the world-space radius small
	// enough that samples stay within a single terrain face at typical outdoor viewing
	// distances (20–100 m). The original 0.15 let the radius reach 4.5 m at 100 m,
	// causing SSAO samples to span several terrain cells and detect the heightmap's
	// own undulation as false occlusion, producing a grid pattern on terrain surfaces.
	float dist = -viewPos.z;
	float adaptiveRadius = uRadius * clamp(dist * 0.05, 1.0, 4.0);

	float occlusion = 0.0;
	float validSamples = 0.0;

	for (int i = 0; i < 16; i++) {
		// Transform kernel sample from tangent space to view space
		vec3 samplePos = viewPos + TBN * uKernel[i] * adaptiveRadius;

		// Project sample to screen space to find where to sample the depth
		vec4 offset = uProjectionMatrix * vec4(samplePos, 1.0);
		offset.xyz /= offset.w;
		offset.xyz = offset.xyz * 0.5 + 0.5;

		// Sample the actual geometry depth at this screen position
		float sampledDepth = texture(uDepthMap, offset.xy).r;
		if (sampledDepth >= 1.0) continue; // no geometry at this sample

		validSamples += 1.0;
		float sampledViewZ = viewPosFromDepth(sampledDepth, offset.xy, uInvProjection).z;

		// Range check: reject samples across large depth discontinuities
		float depthDiff = abs(viewPos.z - sampledViewZ);
		float rangeCheck = 1.0 - smoothstep(adaptiveRadius * 0.5, adaptiveRadius, depthDiff);

		// Smooth occlusion via smoothstep around the bias. A binary comparison
		// (sampledViewZ >= samplePos.z + bias) produces hard 0→1 flips on
		// slightly-tilted close-up faces where kernel slots sit right at the
		// threshold; the smoothstep gives borderline samples a fractional
		// contribution and avoids the resulting visible flicker.
		float depthDelta = sampledViewZ - samplePos.z;
		occlusion += smoothstep(uBias * 0.5, uBias * 2.0, depthDelta) * rangeCheck;
	}

	// Normalize occlusion by valid-sample count so the result is the *ratio*
	// of valid samples that were occluded. The max(validSamples, 4) floor
	// dampens AO at silhouettes / grazing-angle faces where only a few
	// samples land on geometry.
	float ao = 1.0 - occlusion / max(validSamples, 4.0);

	fragColor = vec4(ao, ao, ao, 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: SsaoShaderUniformsContainer;
    public attributesContainer: SsaoShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: SsaoShaderSettings) {
        super(ctx, SsaoShader.fragmentShaderSource, SsaoShader.vertexShaderSource, settings);
        this.uniformsContainer = new SsaoShaderUniformsContainer(ctx, this);
        this.attributesContainer = new SsaoShaderShaderAttributesContainer(ctx, this);
    }
}

class SsaoShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: SsaoShader) {
        super(ctx);
    }

    public setDepthMap(texture: Texture) : SsaoShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setNormalMap(texture: Texture) : SsaoShaderUniformsContainer {
        this.setTextureValue('uNormalMap', this.shader.uniforms.uNormalMap, texture, 'sampler2D');
        return this;
    }

    public setNoiseMap(texture: Texture) : SsaoShaderUniformsContainer {
        this.setTextureValue('uNoiseMap', this.shader.uniforms.uNoiseMap, texture, 'sampler2D');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : SsaoShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setProjectionMatrix(value: mat4 | number[]) : SsaoShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setInvProjection(value: mat4 | number[]) : SsaoShaderUniformsContainer {
        this.setValue('uInvProjection', this.shader.uniforms.uInvProjection, value, 'mat4');
        return this;
    }

    public setKernel(index: number, value: vec3 | number[]) : SsaoShaderUniformsContainer {
        if(index < 0 || index >= 16) throw new Error('Invalid index used for uKernel: ' + index);
        this.setValue('uKernel', this.shader.uniforms.uKernel[index], value, 'vec3');
        return this;
    }

    public setRadius(value: number) : SsaoShaderUniformsContainer {
        this.setValue('uRadius', this.shader.uniforms.uRadius, value, 'float');
        return this;
    }

    public setBias(value: number) : SsaoShaderUniformsContainer {
        this.setValue('uBias', this.shader.uniforms.uBias, value, 'float');
        return this;
    }

    public setNoiseScale(value: vec2 | number[]) : SsaoShaderUniformsContainer {
        this.setValue('uNoiseScale', this.shader.uniforms.uNoiseScale, value, 'vec2');
        return this;
    }

    public setTaaFrameIndex(value: number) : SsaoShaderUniformsContainer {
        this.setValue('uTaaFrameIndex', this.shader.uniforms.uTaaFrameIndex, value, 'int');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' },
			{ name: 'uNormalMap', uniform: this.shader.uniforms.uNormalMap, dataType: 'sampler2D' },
			{ name: 'uNoiseMap', uniform: this.shader.uniforms.uNoiseMap, dataType: 'sampler2D' }
        ];
    }
}

class SsaoShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: SsaoShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : SsaoShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : SsaoShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}